import h from 'virtual-dom/h';
import UrlLinkBuilder from "./urlLinkBuilder";
import InputLinkBuilder from "./inputLinkBuilder";
import UnitLinkBuilder from "./linkUnitBuilder";
import { shouldEnableInputBox } from "../inputBoxLocking/index";
import { DefaultSettings } from "../../../../settings/settings-util";
import { VNode, VTree } from "virtual-dom";
import { HistoryMessageAlme } from "../../../../types/history";
import { Store } from "redux";
import { AlmeState } from "../../../../wireup/AlmeState";
import { DisplayLinkSection } from "@alme/types/response/DisplayLinkSection";
import { DisplayLink } from "@alme/types/response/DisplayLink";
import { Dictionary, Mutator } from "../../../../types/rendering";

type StepOutIcon = { renderIcon: (url: string) => VNode | undefined };
type WebLinkConstants = { moreLinksText?: string; foldLocation?: number; displayLinksHeaderText?: string };

export default class SectionBuilder {

    _settings: DefaultSettings;
    _store: Store<AlmeState> | null = null;
    _urlLinkBuilder: UrlLinkBuilder;
    _unitLinkBuilder: UnitLinkBuilder;
    _inputLinkBuilder: InputLinkBuilder;


    //#region Public Interface

    constructor(settings: DefaultSettings,
        stepOutIcon: StepOutIcon | null = null,
        linkMutators: Mutator[] = []) {
        this._settings = settings;

        this._urlLinkBuilder = new UrlLinkBuilder(settings, stepOutIcon, linkMutators);
        this._unitLinkBuilder = new UnitLinkBuilder(settings, linkMutators);
        this._inputLinkBuilder = new InputLinkBuilder(settings, linkMutators);
    }

    initialize(store: Store<AlmeState>): void {
        this._store = store;

        this._urlLinkBuilder.initialize(store);
        this._unitLinkBuilder.initialize(store);
        this._inputLinkBuilder.initialize(store);
    }

    buildSection(section: DisplayLinkSection, message: HistoryMessageAlme): VTree[] {
        if (!section.Links.filter(link => !link.Metadata.inlineLink).length) { return []; }

        const sectionChildren = [this.buildHeader(section), ...this.buildLinks(section, message)];

        return [h('div', { className: this._settings.classNames.displayLinksSection }, sectionChildren)];
    }

    buildLink(link: DisplayLink, shouldEnable: boolean, message: HistoryMessageAlme, index: number): VNode[] {
        switch (link.TargetType) {
            case "Url":
                return this._urlLinkBuilder.buildUrlLink(link);

            case "Unit":
                return this._unitLinkBuilder.buildUnitLink(link, shouldEnable, message, index);

            case "Input":
                return this._inputLinkBuilder.buildInputLink(link, shouldEnable);

            case "None":
                return [];
        }
    }

    //#endregion



    //#region Builders

    buildHeader(section: DisplayLinkSection): VNode {
        return h('h3', { className: this._settings.classNames.displayLinksSectionHeader }, this.getHeaderText(section));
    }

    buildLinks(section: DisplayLinkSection, message: HistoryMessageAlme): VNode[] {
        const foldLocation = this.getFoldLocation(section);
        const shouldEnable = shouldEnableInputBox(this._settings, this._store);
        const nonInlineLinks = section.Links.filter(link => !link.Metadata.inlineLink);

        const preFold = nonInlineLinks.slice(0, foldLocation).flatMap((link, index) => this.buildLink(link, shouldEnable, message, index));
        const postFold = nonInlineLinks.slice(foldLocation).flatMap((link, index) => this.buildLink(link, shouldEnable, message, index));
        const entryId = message?.extensionData?.entryId as string;
        const fold = postFold.length > 0 ? this.buildFold(section, entryId) : [];
        if (this._settings.enableLessFeature.enabled && message?.extensionData?.entryId) {
            const lessFold = postFold.length > 0 ? this.buildLessFold(section, entryId) : [];
            return [...preFold, ...fold, ...postFold, ...lessFold];
        }
        else {
            return [...preFold, ...fold, ...postFold];
        }
    }

    buildFold(section: DisplayLinkSection, entryId: string): VNode[] {
        const { displayLinksFoldCollapsed, displayLinksFoldExpanded } = this._settings.classNames;
        const { linkFoldTitleText } = this._settings.textConstants;

        const shouldDisable = this._store?.getState().Window.DisplayLinks.shouldDisable;

        const attributes: Dictionary = shouldDisable
            ? { attributes: { tabIndex: "-1" }, className: displayLinksFoldCollapsed}
            : {
                className: displayLinksFoldCollapsed, onclick: (e): boolean => {
                    e.target.className = displayLinksFoldExpanded;
                    this.focusNextLink(e);
                    e.preventDefault();
                    if (this._settings.enableLessFeature.enabled && entryId != null) {
                        let el = document.getElementById(`lesslink-${entryId}`);
                        el?.classList.remove(this._settings.classNames.displayLinksLessFoldExpanded);
                        el?.classList.add(this._settings.classNames.displayLinksLessFoldCollapsed);
                    }
                    return false;
                },
            };
        if (entryId != null) {
            attributes.id = `morelink-${entryId}`;
        }
        if (linkFoldTitleText) { attributes.title = linkFoldTitleText; }

        let parametersMoreLink = {
            className: this._settings.classNames.moreLinkIconClass,
            style: {
                pointerEvents : "none"
            }
        };
        if (this._settings.classNames.moreLinkIconClass !== "") {
            const moreElement = h('i', parametersMoreLink, []);
            return [h('button', attributes, [this._settings.textConstants.moreLinkText, moreElement])];
        }
        else {
            return [h('button', attributes, [this.getFoldText(section)])];
        }
    }

    buildLessFold(section: DisplayLinkSection, entryId: string): VNode[] {
        const { displayLinksLessFoldExpanded, displayLinksLessFoldCollapsed } = this._settings.classNames;
        const { linkFoldTitleText } = this._settings.textConstants;

        const shouldDisable = this._store?.getState().Window.DisplayLinks.shouldDisable;

        const attributes: Dictionary = shouldDisable
            ? { attributes: { tabIndex: "-1" }, className: displayLinksLessFoldExpanded, id:`lesslink-${entryId}` }
            : {
                className: displayLinksLessFoldExpanded, onclick: (e): boolean => {
                    e.target.className = displayLinksLessFoldExpanded;
                    this.focusPreviousLink(e);
                    e.preventDefault();
                    let el = document.getElementById(`morelink-${entryId}`);
                    el?.classList.remove(this._settings.classNames.displayLinksFoldExpanded);
                    el?.classList.add(this._settings.classNames.displayLinksFoldCollapsed);
                    return false;
                },
                id:`lesslink-${entryId}`
            };

        if (linkFoldTitleText) { attributes.title = linkFoldTitleText; }
        let parametersLessElementAttribute = {
            className: this._settings.classNames.lessLinkIconClass,
            style: {
                pointerEvents : "none"
            }
        };
        const lessElement = h('i', parametersLessElementAttribute, []);
        return [h('button', attributes, [this._settings.textConstants.lessLinkText, lessElement])];
    }

    //#endregion



    //#region Getters

    getFoldText(section: DisplayLinkSection): string {
        const constants = (this._settings.weblinkConstants as unknown as WebLinkConstants);

        return constants?.moreLinksText ?? section.FoldText;
    }

    getFoldLocation(section: DisplayLinkSection): number {
        const constants = (this._settings.weblinkConstants as unknown as WebLinkConstants);
        const location = constants?.foldLocation ?? section.FoldLocation;

        return location >= 0 ? location : section.Links.filter(link => !link.Metadata.inlineLink).length;
    }

    getHeaderText(section: DisplayLinkSection): string {
        const constants = (this._settings.weblinkConstants as unknown as WebLinkConstants);

        return constants?.displayLinksHeaderText ?? section.HeaderText;
    }

    //#endregion



    //#region Helpers

    focusNextLink(event: Event): void {
        const children = ((event?.target as HTMLElement)?.nextSibling as HTMLElement)?.children;

        if (children?.length) {
            (children[0] as HTMLElement)?.focus();
        }
    }

    focusPreviousLink(event: Event): void {
        const children = ((event?.target as HTMLElement)?.previousSibling as HTMLElement)?.children;

        if (children?.length) {
             (children[0] as HTMLElement)?.focus();
        }
    }

    //#endregion
}



// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/modules/message-renderer-display-links/src/builders/sectionBuilder.ts